import { useEffect, useRef } from "react";

import { LineStyle, StaticChartLine } from "./types";
import type { IChartingLibraryWidget, IPositionLineAdapter } from "../../charting_library";

const BODY_BACKGROUND_COLOR = "#161616";
const BORDER_COLOR = "#9295ad";
const DEFAULT_LINE_COLOR = "#161616";

export function StaticLine({
  title,
  price,
  quantity,
  lineColor,
  quantityBackgroundColor,
  quantityBorderColor,
  quantityTextColor,
  tvWidgetRef,
}: {
  tvWidgetRef: React.RefObject<IChartingLibraryWidget>;
} & StaticChartLine) {
  const lineApi = useRef<IPositionLineAdapter | undefined>(undefined);
  const latestValues = useRef({
    title,
    price,
    quantity,
    lineColor,
    quantityBackgroundColor,
    quantityBorderColor,
    quantityTextColor,
  });

  latestValues.current = {
    title,
    price,
    quantity,
    lineColor,
    quantityBackgroundColor,
    quantityBorderColor,
    quantityTextColor,
  };

  const syncLine = () => {
    if (!lineApi.current) return;

    lineApi.current
      .setText(latestValues.current.title)
      .setPrice(latestValues.current.price)
      .setQuantity(latestValues.current.quantity ?? "")
      .setLineColor(latestValues.current.lineColor ?? DEFAULT_LINE_COLOR)
      .setQuantityBackgroundColor(latestValues.current.quantityBackgroundColor ?? BODY_BACKGROUND_COLOR)
      .setQuantityBorderColor(latestValues.current.quantityBorderColor ?? BORDER_COLOR)
      .setQuantityTextColor(latestValues.current.quantityTextColor ?? "#fff");
  };

  useEffect(() => {
    const chart = tvWidgetRef.current?.activeChart();
    if (!chart) {
      return;
    }

    chart.dataReady(() => {
      const range = chart.getVisibleRange();

      if (range.from === 0 && range.to === 0) {
        chart.onVisibleRangeChanged().subscribe(null, init, true);
      } else {
        init();
      }
    });

    function init() {
      const positionLine = chart!.createPositionLine({ disableUndo: true });

      lineApi.current = positionLine;

      positionLine
        .setLineStyle(LineStyle.Dotted)
        .setLineLength(-200, "pixel")
        .setBodyFont(`normal 12pt "Relative", sans-serif`)
        .setBodyTextColor("#fff")
        .setBodyBackgroundColor(BODY_BACKGROUND_COLOR)
        .setBodyBorderColor(BORDER_COLOR)
        .setQuantityFont(`normal 12pt "Relative", sans-serif`);

      syncLine();
    }

    return () => {
      lineApi.current?.remove();
      lineApi.current = undefined;
    };
  }, [tvWidgetRef]);

  useEffect(() => {
    syncLine();
  }, [title, price, quantity, lineColor, quantityBackgroundColor, quantityBorderColor, quantityTextColor]);

  return null;
}
